import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount, useToast } from "../context";
import { MessageType, getMessageTimestamp } from "../interfaces/all";
import { getMessagesByClientPhone } from "../services/api";
import { useMessageHandler } from "./useMessageHandler";

interface SearchResult extends MessageType {
  highlightIndexes: number[];
}

export const useMessageSearch = (selectedConversation: any) => {
  const { socket } = useAccount();
  const { showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedMessagesRef = useRef(false);

  const { messages, setMessages } = useMessageHandler({
    socket,
    selectedConversation,
    onNewMessage: () => {},
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const searchCache = useRef<Map<string, SearchResult[]>>(new Map());

  // Função para encontrar todos os índices onde o termo aparece no texto
  const findHighlightIndexes = (text: string, term: string): number[] => {
    const indexes: number[] = [];
    let index = text.toLowerCase().indexOf(term.toLowerCase());
    while (index !== -1) {
      indexes.push(index);
      index = text.toLowerCase().indexOf(term.toLowerCase(), index + 1);
    }
    return indexes;
  };

  // Função para destacar o texto com o termo pesquisado
  const highlightText = (
    text: string,
    indexes: number[],
    termLength: number,
  ): string => {
    if (indexes.length === 0) return text;

    let result = "";
    let lastIndex = 0;

    indexes.forEach((index) => {
      result += text.slice(lastIndex, index);
      result += `<mark>${text.slice(index, index + termLength)}</mark>`;
      lastIndex = index + termLength;
    });

    result += text.slice(lastIndex);
    return result;
  };

  const scrollToMessage = useCallback((messageId: string | undefined) => {
    if (!messageId) {
      console.warn("[MessageSearch] ID da mensagem indefinido");
      return;
    }

    // Aguarda um momento para garantir que o DOM foi atualizado
    setTimeout(() => {
      const messageElement = document.getElementById(`message-${messageId}`);
      if (messageElement) {
        messageElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Adiciona um highlight temporário
        messageElement.style.backgroundColor = "rgba(255, 235, 59, 0.2)";
        setTimeout(() => {
          messageElement.style.backgroundColor = "";
        }, 2000);
      } else {
        console.warn("[MessageSearch] Elemento não encontrado:", messageId);
      }
    }, 100);
  }, []);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setCurrentResultIndex(-1);

      if (!term.trim()) {
        setSearchResults([]);
        return;
      }

      // Verifica se já temos os resultados em cache
      const cacheKey = `${selectedConversation?._id}-${term}`;
      const cachedResults = searchCache.current.get(cacheKey);

      if (cachedResults) {
        setSearchResults(cachedResults);
        return;
      }

      const termLower = term.toLowerCase();

      const results: SearchResult[] = messages
        .filter((message) => {
          const text = message.content?.text?.toLowerCase() || "";
          const matches = text.includes(termLower);
          return matches;
        })
        .map((message) => {
          const text = message.content?.text || "";
          const highlightIndexes = findHighlightIndexes(text, term);
          return {
            ...message,
            highlightIndexes,
            content: {
              ...message.content,
              text: highlightText(text, highlightIndexes, term.length),
            },
          };
        });

      // Armazena no cache
      searchCache.current.set(cacheKey, results);
      setSearchResults(results);
    },
    [messages, selectedConversation?._id],
  );

  // Navegação entre resultados
  const navigateResults = useCallback(
    (direction: "next" | "prev") => {
      if (searchResults.length === 0) return;

      setCurrentResultIndex((prevIndex) => {
        const newIndex =
          direction === "next"
            ? (prevIndex + 1) % searchResults.length
            : (prevIndex - 1 + searchResults.length) % searchResults.length;

        return newIndex;
      });
    },
    [searchResults],
  );

  // Carrega as mensagens iniciais
  useEffect(() => {
    const loadInitialMessages = async () => {
      if (!selectedConversation || hasLoadedMessagesRef.current) return;

      const phoneNumber =
        selectedConversation.valid_telephone || selectedConversation.telephone;

      if (!phoneNumber) {
        return;
      }

      setIsLoading(true);
      try {
        const response = await getMessagesByClientPhone(phoneNumber);

        if (response.status === "success" && "data" in response) {
          const newMessages = Array.isArray((response.data as any).messages)
            ? (response.data as any).messages
            : [];

          setMessages((prev) => {
            const allMessages = [...prev, ...newMessages];
            const uniqueMessages = allMessages.filter(
              (message, index, self) =>
                index === self.findIndex((m) => m._id === message._id),
            );

            const sortedMessages = uniqueMessages.sort(
              (a, b) =>
                getMessageTimestamp(a).getTime() -
                getMessageTimestamp(b).getTime(),
            );

            return sortedMessages;
          });

          hasLoadedMessagesRef.current = true;
        } else {
          console.warn("[MessageSearch] Erro ao carregar mensagens:", response);
          showError(response);
        }
      } catch (error) {
        console.error("[MessageSearch] Erro ao carregar mensagens:", error);
        showError(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialMessages();
  }, [selectedConversation, setMessages, showError]);

  // Reseta o estado quando muda de conversa
  useEffect(() => {
    if (selectedConversation) {
      hasLoadedMessagesRef.current = false;
      setSearchTerm("");
      setSearchResults([]);
      setCurrentResultIndex(-1);
      searchCache.current.clear();
    }
  }, [selectedConversation?._id]);

  // Atalhos de teclado para navegação
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!searchTerm) return;

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        navigateResults("next");
      } else if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        navigateResults("prev");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [searchTerm, navigateResults]);

  // Cleanup adequado para evitar vazamentos de memória
  useEffect(() => {
    return () => {
      // Limpar cache e estados
      searchCache.current.clear();
      setSearchResults([]);
      setCurrentResultIndex(-1);
      hasLoadedMessagesRef.current = false;
    };
  }, []);

  return {
    searchTerm,
    searchResults,
    currentResultIndex,
    setCurrentResultIndex,
    handleSearch,
    navigateResults,
    isLoading,
    scrollToMessage,
  };
};
